import { ref, computed, onUnmounted } from 'vue';
import debounce from 'lodash/debounce';
import store from '@/store';
import { config } from '@/config.js';
import { useRoute, useRouter } from '@/composables/common/router/useRouter';
import { queriesEqual } from '@/composables/common/useQueryUtils';

export function useEquiposShow() {
    const route = useRoute();
    const router = useRouter();

    const listaId = computed(() => route.params.lista_id);
    const equipoId = computed(() => route.params.equipo_id);

    const deportistas = ref(null);
    const categorias = ref([]);

    const loading = ref(false);

    const filters = ref({
        search: '',
        search_for: '',
        categoria: '',
        metodo_pago: '',
        page: 1,
        per_page: 10
    });

    const teatro = computed(() => store.getters.getTeatro);
    const metodosPagoAbonosBases = computed(
        () => teatro.value?.metodos_pago_abonos_bases || []
    );

    // --- URL Sync ---
    const isHydrating = ref(false);

    function updateUrlQuery() {
        const f = filters.value;
        const newQuery = {};

        if (f.search) newQuery.search = f.search;
        if (f.search_for) newQuery.search_for = f.search_for;
        if (f.categoria) newQuery.categoria = f.categoria;
        if (f.metodo_pago) newQuery.metodo_pago = f.metodo_pago;
        if (f.page && f.page > 1) newQuery.page = f.page;
        if (f.per_page && f.per_page !== 10) newQuery.per_page = f.per_page;

        if (queriesEqual(newQuery, route.query)) return;

        router.replace({ name: route.name, query: newQuery }).catch(() => {});
    }

    const updateUrlDebounced = debounce(() => updateUrlQuery(), 500);

    /**
     * Fetch deportistas (bases) paginated list with current filters.
     */
    async function fetch() {
        const f = filters.value;
        const params = {
            filters: {
                per_page: f.per_page,
                page: f.page,
                search: f.search,
                search_for: f.search_for,
                categoria: f.categoria,
                metodo_pago: f.metodo_pago,
                lista_id: listaId.value
            },
            lista_id: listaId.value,
            equipo_id: equipoId.value
        };

        loading.value = true;
        try {
            const res = await store.dispatch(
                config.getVuexAlias('back.bases.equipos.get-bases'),
                params
            );
            deportistas.value = res.data;
        } catch (error) {
            console.error('Error fetching deportistas:', error);
        } finally {
            loading.value = false;
        }
    }

    const debouncedFetch = debounce(() => fetch(), 500);

    /**
     * Fetch categorias for the filter dropdown.
     */
    async function fetchCategorias() {
        try {
            const res = await store.dispatch(
                config.getVuexAlias('back.bases.get.categorias'),
                { filters: {}, lista_id: listaId.value }
            );
            categorias.value = res.data.data;
        } catch (error) {
            console.error('Error fetching categorias:', error);
        }
    }

    function init() {
        fetchCategorias();
        fetch();
    }

    onUnmounted(() => {
        debouncedFetch.cancel();
        updateUrlDebounced.cancel();
    });

    return {
        // state
        filters,
        deportistas,
        categorias,
        loading,
        // URL sync
        isHydrating,
        // computed
        listaId,
        metodosPagoAbonosBases,
        // methods
        fetch,
        debouncedFetch,
        fetchCategorias,
        init,
        queriesEqual,
        updateUrlQuery,
        updateUrlDebounced
    };
}
